import { Backdrop } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { MainContent } from '../dashboard/style';
import { useDeviceType } from '../hooks/useDeviceType';
import { Loading, useLoading } from '../hooks/useLoading';
import { AccountOriginProvider } from '../hooks/useAccountOrigin';
import { useService, useServiceContainer } from '@aesop-fables/containr-react';
import { Authentication, Data, Domain, Hooks } from '@3nickels/data-modules';
import { useAuthenticatedFlag, useAuthenticationStatus, useNeedsOnboarding } from '../hooks';
import { RouteObject, RouterProvider, createBrowserRouter, matchRoutes } from 'react-router';
import { ContentViewProvider } from '../pages/layout/ContentViewContext';
import { useMessage } from '../hooks/useMessage';
import { t } from 'i18next';
import { OverlayProvider } from '../pages/layout/OverlayContext';
import AppModal from '../pages/layout/AppModal';
import RouteErrorBoundary from '../components/RouteErrorBoundary';
import { RouteTransitionGateProvider } from './RouteTransitionGate';
// Lazy-loaded to keep @embrace-io out of the critical bundle.
// listenToRouterChanges is resolved via dynamic import in the useEffect below.

function withErrorBoundary(routes: RouteObject[]): RouteObject[] {
  return [{ errorElement: <RouteErrorBoundary />, children: routes }];
}

function AppGateway() {
  const { loading } = useLoading();
  const authContext = useService<Authentication.IAuthenticationContext>(
    Authentication.AuthenticationServices.Context
  );
  const isAuthenticated = useAuthenticatedFlag();
  const needsOnboarding = useNeedsOnboarding();
  const productType = Hooks.useProductType();
  const freeTrialInfo = Hooks.useFreeTrialInfo();
  const { isReady, isSessionExpired } = useAuthenticationStatus();
  const { showMessage } = useMessage();
  const { isMobile, isTablet } = useDeviceType();
  const container = useServiceContainer();
  const logger = Hooks.useLogger();
  const [router, setRouter] = useState<ReturnType<typeof createBrowserRouter> | null>(null);
  const routerKeyRef = useRef<string | null>(null);
  const splashDismissedRef = useRef(false);
  const [revealed, setRevealed] = useState(false);
  // Held true while a multi-screen flow (e.g. accepting holistic advice) is
  // still navigating within the advice-onboarding route set, even though the
  // underlying flag driving `needsOnboarding` has already flipped. See
  // RouteTransitionGate.tsx.
  const [skipRemount, setSkipRemount] = useState(false);

  useEffect(() => {
    if (isReady && isSessionExpired) {
      authContext.setIsAuthenticated(false);
      showMessage(t('YouHaveBeenLoggedOutDueToInactivity') as string, 'error');
    }
  }, [isSessionExpired, isReady]);

  // Build router lazily to keep the initial bundle smaller.
  useEffect(() => {
    let cancelled = false;

    const setRouterIfNeeded = (
      key: string,
      build: () => ReturnType<typeof createBrowserRouter>
    ) => {
      if (cancelled) return;
      if (routerKeyRef.current === key) return;
      const newRouter = build();
      routerKeyRef.current = key;
      setRouter(newRouter);
    };

    const buildRouter = async () => {
      if (!isAuthenticated) {
        const { createUnauthenticatedRoutes } = await import('./UnauthenticatedRoutes');
        setRouterIfNeeded('unauth', () =>
          createBrowserRouter(withErrorBoundary(createUnauthenticatedRoutes(container)))
        );
        return;
      }

      // Don't create a router until we know which route set to use.
      // Showing <Loading /> via the canRender guard is cheaper than mounting
      // an intermediate "loading" router that triggers a new hydration cycle
      // when swapped for the real one.
      if (typeof needsOnboarding === 'undefined') {
        return;
      }

      if (isMobile || isTablet) {
        const { createMobileRoutes } = await import('./MobilesRoutes');
        setRouterIfNeeded('mobile', () =>
          createBrowserRouter(withErrorBoundary(createMobileRoutes(container)))
        );
        return;
      }

      if (freeTrialInfo?.showTrialExpiredScreen) {
        const { createFreeRoutes } = await import('./FreeRoutes');
        setRouterIfNeeded('free', () =>
          createBrowserRouter(withErrorBoundary(createFreeRoutes(container)))
        );
        return;
      }

      if (productType === Domain.ProductTypeEnum.advice) {
        if (needsOnboarding) {
          const { createAdviceOnboardingRoutes } = await import('./AdviceOnboardingRoutes');
          setRouterIfNeeded('advice-onboarding', () =>
            createBrowserRouter(withErrorBoundary(createAdviceOnboardingRoutes(container)))
          );
          return;
        }

        // Coming from advice-onboarding: accepting advice flips
        // `adviceOnboarded` mid-flow (see AcceptHolisticAdvice /
        // CompleteAdviceOnboarding), before the multi-screen accept flow has
        // finished navigating within the onboarding route set. Swapping here
        // would remount the whole tree and drop the in-flight navigation.
        // Defer until the flow explicitly releases the gate.
        if (routerKeyRef.current === 'advice-onboarding' && skipRemount) {
          return;
        }

        const { createAdviceRoutes } = await import('./AdviceRoutes');
        setRouterIfNeeded('advice', () =>
          createBrowserRouter(withErrorBoundary(createAdviceRoutes(container)))
        );
        return;
      }

      const { createFreeRoutes } = await import('./FreeRoutes');
      setRouterIfNeeded('free', () =>
        createBrowserRouter(withErrorBoundary(createFreeRoutes(container)))
      );
    };

    buildRouter().catch((err) => console.error('[AppGateway] buildRouter error:', err));
    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    needsOnboarding,
    isMobile,
    isTablet,
    productType,
    freeTrialInfo?.showTrialExpiredScreen,
    container,
    skipRemount,
  ]);

  // Safety net: if the user leaves the holistic-advice flow by any means
  // other than the flow's own unlock call (browser back, direct nav, an
  // error mid-flow), stop holding the gate once we're no longer inside it so
  // a missed unlock can't permanently strand the app on the onboarding route
  // set.
  useEffect(() => {
    if (!skipRemount || !router || routerKeyRef.current !== 'advice-onboarding') return;

    const unsubscribe = router.subscribe((state) => {
      if (!state.location.pathname.startsWith('/holistic-advice')) {
        setSkipRemount(false);
      }
    });
    return unsubscribe;
  }, [skipRemount, router]);

  useEffect(() => {
    if (!router) return;
    import('@embrace-io/web-sdk/react-instrumentation')
      .then(({ listenToRouterChanges }) => {
        listenToRouterChanges({
          router,
          routesMatcher: matchRoutes,
        });
      })
      .catch(() => {});
  }, [router]);

  useEffect(() => {
    logger.debug(`AppGateway loading: ${loading}`);
  }, [loading]);

  // Don't render the unauth router for an authenticated user — the effect to
  // rebuild the router hasn't fired yet (effects run after render).  Showing
  // the wrong router causes a flash of the login page and then a blank screen
  // while React Router re-hydrates the replacement router without a fallback.
  const routerMatchesAuth = !isAuthenticated || routerKeyRef.current !== 'unauth';
  const canRender = isReady && router && routerMatchesAuth;

  // Dismiss the HTML splash overlay once the app is ready to render.
  useEffect(() => {
    if (!canRender || splashDismissedRef.current) return;

    const splash = document.getElementById('splash-loader');
    if (!splash) {
      // No splash element (e.g. alternate HTML shell, test harness) — reveal immediately.
      splashDismissedRef.current = true;
      setRevealed(true);
      return;
    }

    splashDismissedRef.current = true;
    setRevealed(true);

    const remove = () => splash.remove();
    splash.addEventListener('transitionend', remove, { once: true });
    // Fallback in case transitionend doesn't fire (e.g. prefers-reduced-motion).
    const id = setTimeout(remove, 1000);
    splash.classList.add('splash-fade-out');

    return () => {
      clearTimeout(id);
      splash.removeEventListener('transitionend', remove);
    };
  }, [canRender]);

  return (
    <MainContent isMobile={isMobile || isTablet}>
      <Backdrop open={loading}>
        <Loading />
      </Backdrop>
      {canRender ? (
        <div className={revealed ? 'content-enter' : 'content-pre-reveal'}>
          <RouteTransitionGateProvider value={{ skipRemount, setSkipRemount }}>
            <ContentViewProvider>
              <Hooks.PlaidWizardProvider>
                <Data.Transactions.TransactionsSummaryProvider>
                  <Data.Transactions.TransactionsCategorySearchProvider>
                    <AccountOriginProvider>
                      <Data.Charity.CharityProvider>
                        <OverlayProvider value={{ overlay: false }}>
                          <RouterProvider key={routerKeyRef.current} router={router} />
                          <AppModal />
                        </OverlayProvider>
                      </Data.Charity.CharityProvider>
                    </AccountOriginProvider>
                  </Data.Transactions.TransactionsCategorySearchProvider>
                </Data.Transactions.TransactionsSummaryProvider>
              </Hooks.PlaidWizardProvider>
            </ContentViewProvider>
          </RouteTransitionGateProvider>
        </div>
      ) : null}
    </MainContent>
  );
}

export default AppGateway;
